/**
 * Zahlen für die öffentliche Webseite: eine Quelle für alle Seiten (Frage 293, Daniel 14.10.2026; Sitzung
 * „Aufräumen“). Wer auf einer öffentlichen Seite eine Zahl zu Kursen, Lektionen, Fachkräften oder Jahren zeigt,
 * nimmt sie von hier und schreibt sie nicht fest in die Seite. `webseiteFakten.test.ts` sucht die alten Zahlen
 * (2.500, 5.000, 6.000 Inhalte, 20.000 Nutzer, 4,9 von 5 …) in den öffentlichen Seiten.
 *
 * Belege:
 * - Kurse: veröffentlicht und nicht versteckt, gezählt am 14.10.2026 (215, davon 210 mit Landingpage).
 * - Lektionen: in veröffentlichten Kursen, ohne Entwürfe, gezählt am 14.10.2026 (7.846, davon 2.978 Videos).
 * - Fachkräfte: über 30.000 Registrierungen auf der alten Thinkific-Plattform, dazu über 3.000 neue Konten
 *   (Daniel, Frage 293). Gezeigt wird bewusst die vorsichtige Zahl.
 * - Seit 1999: Daniel gründet gitarrissimo, Kinderliedbegleitung auf der Gitarre für pädagogische Fachkräfte.
 *
 * Eine Bewertung („4,9 von 5“) gibt es nicht (Frage 297): Sie hatte keine Quelle und kommt erst mit einer zurück.
 * Kurse und Lektionen werden nach unten auf eine runde Zahl gerundet; live aus der Datenbank kommen sie mit dem
 * Relaunch der Webseite (Sitzung 9). Bis dahin nach einer neuen Zählung hier nachziehen.
 */
export const WEBSEITE_FAKTEN = {
  gezaehltAm: "14.10.2026",
  kurse: { gezaehlt: 215, gerundet: 200 },
  lektionen: { gezaehlt: 7846, gerundet: 7500 },
  fachkraefte: { gerundet: 10000 },
  gegruendet: 1999,
} as const;

/** 10000 → „10.000“ (ohne toLocaleString, das je nach Umgebung anders trennt) */
const tausender = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/** Gerundete Zahlen als Text: „200“, „7.500“, „10.000“ (für eigene Sätze, z. B. „mit über 200 Kursen“) */
export const KURSE_ZAHL = tausender(WEBSEITE_FAKTEN.kurse.gerundet);
export const LEKTIONEN_ZAHL = tausender(WEBSEITE_FAKTEN.lektionen.gerundet);
export const FACHKRAEFTE_ZAHL = tausender(WEBSEITE_FAKTEN.fachkraefte.gerundet);

/** „über 200 Kurse“ */
export const KURSE_TEXT = `über ${tausender(WEBSEITE_FAKTEN.kurse.gerundet)} Kurse`;
/** „über 7.500 Lektionen“ */
export const LEKTIONEN_TEXT = `über ${tausender(WEBSEITE_FAKTEN.lektionen.gerundet)} Lektionen`;
/** „über 10.000 Fachkräfte“ */
export const FACHKRAEFTE_TEXT = `über ${tausender(WEBSEITE_FAKTEN.fachkraefte.gerundet)} Fachkräfte`;
/** „seit 1999“ */
export const SEIT_TEXT = `seit ${WEBSEITE_FAKTEN.gegruendet}`;

/** Für Zählwerke und Kacheln: „200+“, „7.500+“, „10.000+“ */
export const KURSE_KURZ = `${tausender(WEBSEITE_FAKTEN.kurse.gerundet)}+`;
export const LEKTIONEN_KURZ = `${tausender(WEBSEITE_FAKTEN.lektionen.gerundet)}+`;
export const FACHKRAEFTE_KURZ = `${tausender(WEBSEITE_FAKTEN.fachkraefte.gerundet)}+`;

/**
 * Lieder der Mitgliedschaft für den Ausblick auf /kinderlieder (Sitzung 12d): Audio-Lektionen in veröffentlichten,
 * nicht versteckten Kursen außerhalb der Ausbildungen, ohne Entwürfe, je Titel einmal (bereinigt wie in
 * `webseite_lieder_ausblick()`, ohne Übungen und Erklärungen), gezählt am 17.10.2026: 430. Gerundet nach unten.
 */
export const LIEDER_FAKTEN = { gezaehltAm: "17.10.2026", gezaehlt: 430, gerundet: 400 } as const;
/** „über 400 Lieder“ */
export const LIEDER_TEXT = `über ${tausender(LIEDER_FAKTEN.gerundet)} Lieder`;

/** Großschreibung am Satzanfang: „Über 200 Kurse“ */
export const gross = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * „Was bekomme ich für meine Taler?“ auf der Preisseite (Sitzung 10): Stand von `labs_taler_beispiele_stand`, gerechnet
 * am 27.10.2026 mit den Preisen aus Sitzung TP (Video nach Länge, Begrüßung geschenkt) und Sitzung TB (ein Video im
 * Kurzimpuls, Stundenbild oder in der Themenreise ist eine Stelle für 1 Taler). Live kommt er aus
 * `webseite_taler_beispiele()` (Migration 20261016170000, ohne Anmeldung); diese Zahlen gelten nur, solange die Antwort
 * fehlt. Gleiche Schlüssel wie die Datenbank (`labs_taler_beispiele_rechnen()`), ein Test hält sie gleich.
 */
export const TALER_BEISPIELE_STAND = {
  gerechnetAm: "27.10.2026",
  werte: {
    lektion_schnitt: 1.42,
    lektionen: 6596,
    geschenkt: 709,
    kreativpaket: 25,
    kreativpaket_lektionen: 16,
    webinar: 30,
    webinar_lektionen: 22,
    lektionen_kurzimpuls: 3,
    lektionen_stundenbild: 4,
    lektionen_themenreise: 7,
    miko: { frage: 1, idee: 1, videofrage: 1, kurzimpuls: 2, stundenbild: 3, themenreise: 5, tonart_lied: 1 },
  },
} as const;

/**
 * Einzelpreise der Termine, die für Mitglieder frei sind (`isMemberFreeTerminTyp`), aus `termine.preis_normal` am
 * 16.10.2026: jede Liederwerkstatt 12 €, jedes Themenwebinar 49 €. Für den Satz „Was in der Mitgliedschaft steckt“.
 */
export const TERMIN_EINZELPREISE = {
  gezaehltAm: "16.10.2026",
  liederwerkstattEuro: 12,
  themenwebinarEuro: 49,
} as const;
